"use client"

import styled from "@emotion/styled"
import { useLocale } from "next-intl"
import Link from "next/link"
import { useCallback, useState } from "react"
import { LandingPhoneIcon } from "ui"
import { extractProperty, getLinkHrefFromMultiUrlPickerItem } from "utils"
import { useLandingAnalytics } from "../analytics"
import { useAnchorScroll } from "../useAnchorScroll"

type Props = {
  data: any
}

const BP_DESKTOP = "@media (min-width: 1024px)"

// --- Styled Components ---

const Wrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 98;

  ${BP_DESKTOP} {
    bottom: 40px;
    left: auto;
    right: 40px;
  }
`

/* Mobile: full-width white bar anchored to bottom */
const MobileBar = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  background: white;
  box-shadow: 0px -4px 4px rgba(0, 0, 0, 0.12);
  padding: 12px 16px;
  align-items: center;

  ${BP_DESKTOP} {
    display: none;
  }
`

const MobileButtonOutlined = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
  background-color: white;
  color: #020a0d;
  border: 1px solid #020a0d;
  font-family: inherit;
  transition: opacity 0.2s ease;

  height: 40px;

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed #020a0d;
    outline-offset: 2px;
  }
`

const MobileButtonFilled = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
  background-color: #020a0d;
  color: white;
  font-family: inherit;
  transition: opacity 0.2s ease;

  height: 40px;

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: 2px;
  }
`

const MobilePhoneCircle = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #020a0d;
  color: white;
  flex-shrink: 0;
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: 2px;
  }
`

/* Desktop: collapsed button or flyout */
const DesktopArea = styled.div`
  display: none;

  ${BP_DESKTOP} {
    display: flex;
  }
`

const CollapsedButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  color: #020a0d;
  border: 1px solid #020a0d;
  border-radius: 8px;
  font-size: 20px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s ease;
  font-family: inherit;
  width: 174px;
  height: 48px;

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px dashed white;
    outline-offset: 2px;
  }
`

const FlyoutCard = styled.div<{ hasPromo: boolean }>`
  background: ${({ hasPromo }) => (hasPromo ? "white" : "transparent")};
  border-radius: ${({ hasPromo }) => (hasPromo ? "12px" : "0")};
  box-shadow: ${({ hasPromo }) => (hasPromo ? "0px 4px 6px rgba(0, 0, 0, 0.12)" : "none")};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: ${({ hasPromo }) => (hasPromo ? "20px 16px 16px" : "0")};
  position: relative;
  width: ${({ hasPromo }) => (hasPromo ? "262px" : "auto")};
  min-height: ${({ hasPromo }) => (hasPromo ? "360px" : "auto")};
`

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  left: 10px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: #020a0d;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px dashed #020a0d;
    outline-offset: 2px;
  }
`

const PromoTitle = styled.h3`
  font-size: 32px;
  font-weight: 700;
  line-height: 1;
  color: #020a0d;
  text-align: center;
  margin: 0;
  width: 100%;
`

const SubtitlePhoneGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const PromoSubtitle = styled.p`
  font-size: 20px;
  font-weight: 400;
  line-height: 1.5;
  color: #020a0d;
  text-align: center;
  margin: 0;
`

const PhoneNumber = styled(Link)`
  font-size: 30px;
  font-weight: 700;
  line-height: 1.3;
  color: #020a0d;
  text-decoration: none;
  text-align: center;

  &:focus-visible {
    outline: 2px dashed #020a0d;
    outline-offset: 2px;
  }
`

/* Shared by the divider and the CTA buttons so the line ends exactly where the
   buttons end (ASH-2123). At 100% the divider ran the full 230px content width
   of the card while the buttons are narrower, so it overhung them on both
   sides. */
const ACTION_WIDTH = "192px"

const Divider = styled.div`
  display: flex;
  align-items: center;
  width: ${ACTION_WIDTH};
  gap: 6px;
`

/* full strength rather than a 20% tint — QA read the old line as too faint */
const DividerLine = styled.span`
  flex: 1;
  height: 1px;
  background-color: #020a0d;
`

const DividerText = styled.span`
  font-size: 20px;
  font-weight: 400;
  color: #020a0d;
  text-align: center;
`

const ButtonsColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
`

const ActionButton = styled(Link)<{ variant: "outlined" | "filled" }>`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 20px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: opacity 0.2s ease;
  font-family: inherit;
  width: ${ACTION_WIDTH};
  height: 53px;

  background-color: ${({ variant }) => (variant === "filled" ? "#020a0d" : "white")};
  color: ${({ variant }) => (variant === "filled" ? "white" : "#020a0d")};
  border: ${({ variant }) => (variant === "filled" ? "none" : "1px solid #020a0d")};

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px dashed ${({ variant }) => (variant === "filled" ? "white" : "#020a0d")};
    outline-offset: 2px;
  }
`

const SimpleCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: white;
  border: 1px solid #020a0d;
  cursor: pointer;
  color: #020a0d;

  &:hover {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px dashed #020a0d;
    outline-offset: 2px;
  }
`

function CloseIcon() {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M4 4l8 8M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function StickyContact({ data }: Props) {
  const analytics = useLandingAnalytics()
  const locale = useLocale()
  const isRtl = locale === "he" || !locale
  const [isOpen, setIsOpen] = useState(false)

  const label = extractProperty(data, "stickyContactLabel") as string | undefined
  const title = extractProperty(data, "stickyContactTitle") as string | undefined
  const subtitle = extractProperty(data, "stickyContactSubtitle") as string | undefined
  const phoneLink = extractProperty(data, "stickyContactPhone") as any[] | undefined
  const button1Link = extractProperty(data, "stickyContactButton1") as any[] | undefined
  const button2Link = extractProperty(data, "stickyContactButton2") as any[] | undefined

  const phoneLinkItem = phoneLink?.[0]
  const button1Item = button1Link?.[0]
  const button2Item = button2Link?.[0]

  const hasPromo = !!(title || subtitle || phoneLinkItem)

  const handleAnchorClick = useAnchorScroll(useCallback(() => setIsOpen(false), []))

  return (
    <Wrapper>
      {/* Mobile: bottom bar */}
      <MobileBar>
        {hasPromo ? (
          <>
            {/* RTL: first JSX = right. filled (right) → outlined → phone (left) */}
            {button2Item?.url && (
              <MobileButtonFilled
                href={getLinkHrefFromMultiUrlPickerItem(button2Item)}
                target={button2Item.target || undefined}
                rel={button2Item.target === "_blank" ? "noopener noreferrer" : undefined}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  analytics.stickyButtonClick(button2Item.name)
                  handleAnchorClick(e, getLinkHrefFromMultiUrlPickerItem(button2Item))
                }}
              >
                {button2Item.name}
              </MobileButtonFilled>
            )}
            {button1Item?.url && (
              <MobileButtonOutlined
                href={getLinkHrefFromMultiUrlPickerItem(button1Item)}
                target={button1Item.target || undefined}
                rel={button1Item.target === "_blank" ? "noopener noreferrer" : undefined}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  analytics.stickyButtonClick(button1Item.name)
                  handleAnchorClick(e, getLinkHrefFromMultiUrlPickerItem(button1Item))
                }}
              >
                {button1Item.name}
              </MobileButtonOutlined>
            )}
            {phoneLinkItem?.url && (
              <MobilePhoneCircle
                href={getLinkHrefFromMultiUrlPickerItem(phoneLinkItem)}
                aria-label={isRtl ? "התקשר" : "Call"}
                onClick={() => analytics.c2cClick(phoneLinkItem.name || phoneLinkItem.url)}
              >
                <LandingPhoneIcon />
              </MobilePhoneCircle>
            )}
          </>
        ) : (
          <>
            {/* RTL: first JSX = right. filled (right) → outlined (left) */}
            {button2Item?.url && (
              <MobileButtonFilled
                href={getLinkHrefFromMultiUrlPickerItem(button2Item)}
                target={button2Item.target || undefined}
                rel={button2Item.target === "_blank" ? "noopener noreferrer" : undefined}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  analytics.stickyButtonClick(button2Item.name)
                  handleAnchorClick(e, getLinkHrefFromMultiUrlPickerItem(button2Item))
                }}
              >
                {button2Item.name}
              </MobileButtonFilled>
            )}
            {button1Item?.url && (
              <MobileButtonOutlined
                href={getLinkHrefFromMultiUrlPickerItem(button1Item)}
                target={button1Item.target || undefined}
                rel={button1Item.target === "_blank" ? "noopener noreferrer" : undefined}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  analytics.stickyButtonClick(button1Item.name)
                  handleAnchorClick(e, getLinkHrefFromMultiUrlPickerItem(button1Item))
                }}
              >
                {button1Item.name}
              </MobileButtonOutlined>
            )}
          </>
        )}
      </MobileBar>

      {/* Desktop: collapsed button or flyout */}
      <DesktopArea>
        {!isOpen ? (
          <CollapsedButton onClick={() => setIsOpen(true)}>
            {label || (isRtl ? "יצירת קשר" : "Contact Us")}
          </CollapsedButton>
        ) : hasPromo ? (
          <FlyoutCard hasPromo>
            <CloseButton onClick={() => setIsOpen(false)} aria-label={isRtl ? "סגור" : "Close"}>
              <CloseIcon />
            </CloseButton>

            {title && <PromoTitle>{title}</PromoTitle>}

            {(subtitle || phoneLinkItem) && (
              <SubtitlePhoneGroup>
                {subtitle && <PromoSubtitle>{subtitle}</PromoSubtitle>}
                {phoneLinkItem?.url && (
                  <PhoneNumber
                    href={getLinkHrefFromMultiUrlPickerItem(phoneLinkItem)}
                    onClick={() => analytics.c2cClick(phoneLinkItem.name || phoneLinkItem.url)}
                  >
                    {phoneLinkItem.name || phoneLinkItem.url?.replace("tel:", "")}
                  </PhoneNumber>
                )}
              </SubtitlePhoneGroup>
            )}

            {(button1Item || button2Item) && (
              <>
                <Divider>
                  <DividerLine />
                  <DividerText>{isRtl ? "או" : "or"}</DividerText>
                  <DividerLine />
                </Divider>
                <ButtonsColumn>
                  {button1Item?.url && (
                    <ActionButton
                      variant="outlined"
                      href={getLinkHrefFromMultiUrlPickerItem(button1Item)}
                      target={button1Item.target || undefined}
                      rel={button1Item.target === "_blank" ? "noopener noreferrer" : undefined}
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        analytics.c2cClick(button1Item.name)
                        handleAnchorClick(e, getLinkHrefFromMultiUrlPickerItem(button1Item))
                      }}
                    >
                      {button1Item.name}
                    </ActionButton>
                  )}
                  {button2Item?.url && (
                    <ActionButton
                      variant="filled"
                      href={getLinkHrefFromMultiUrlPickerItem(button2Item)}
                      target={button2Item.target || undefined}
                      rel={button2Item.target === "_blank" ? "noopener noreferrer" : undefined}
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        analytics.c2cClick(button2Item.name)
                        handleAnchorClick(e, getLinkHrefFromMultiUrlPickerItem(button2Item))
                      }}
                    >
                      {button2Item.name}
                    </ActionButton>
                  )}
                </ButtonsColumn>
              </>
            )}
          </FlyoutCard>
        ) : (
          <FlyoutCard hasPromo={false}>
            {button1Item?.url && (
              <ActionButton
                variant="outlined"
                href={getLinkHrefFromMultiUrlPickerItem(button1Item)}
                target={button1Item.target || undefined}
                rel={button1Item.target === "_blank" ? "noopener noreferrer" : undefined}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  analytics.c2cClick(button1Item.name)
                  handleAnchorClick(e, getLinkHrefFromMultiUrlPickerItem(button1Item))
                }}
              >
                {button1Item.name}
              </ActionButton>
            )}
            {button2Item?.url && (
              <ActionButton
                variant="filled"
                href={getLinkHrefFromMultiUrlPickerItem(button2Item)}
                target={button2Item.target || undefined}
                rel={button2Item.target === "_blank" ? "noopener noreferrer" : undefined}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  analytics.c2cClick(button2Item.name)
                  handleAnchorClick(e, getLinkHrefFromMultiUrlPickerItem(button2Item))
                }}
              >
                {button2Item.name}
              </ActionButton>
            )}
            <SimpleCloseButton
              onClick={() => setIsOpen(false)}
              aria-label={isRtl ? "סגור" : "Close"}
            >
              <CloseIcon />
            </SimpleCloseButton>
          </FlyoutCard>
        )}
      </DesktopArea>
    </Wrapper>
  )
}

export default StickyContact
